import { useSyncExternalStore } from 'react';
import { Pressable, View } from 'react-native';
import { Text } from './components/primitives';
import { colors } from './tokens';

/** Version stamped into a PWA build by tools/pwa/vite-plugin.mjs. */
export type AppBuild = { version: string; build: string };

export const formatAppBuild = ({ version, build }: AppBuild): string => `Phiên bản ${version} · ${build}`;

const CHECK_EVERY_MS = 15 * 60_000;

let updateReady = false;
const listeners = new Set<() => void>();
const markUpdateReady = () => {
  updateReady = true;
  listeners.forEach((notify) => notify());
};

/**
 * Registers /sw.js and keeps the app on the latest deploy. The browser is asked
 * for a new worker every 15 minutes and whenever the app comes back to the
 * foreground; a new worker activates straight away. The page then reloads on its
 * own if it is in the background, otherwise UpdateBanner offers the reload so a
 * customer is never interrupted mid-order.
 */
export const startAutoUpdate = (): void => {
  if (!('serviceWorker' in navigator)) return;
  // the very first install also fires controllerchange, but that page is already current
  const hadWorker = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadWorker || updateReady) return;
    if (document.visibilityState === 'hidden') window.location.reload();
    else markUpdateReady();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && updateReady) window.location.reload();
  });

  void navigator.serviceWorker.register('/sw.js').then((registration) => {
    const check = () => void registration.update().catch(() => undefined);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
    setInterval(check, CHECK_EVERY_MS);
  });
};

const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => listeners.delete(notify);
};

export const useUpdateReady = (): boolean => useSyncExternalStore(subscribe, () => updateReady, () => false);

/** Pinned to the top of the app frame once a newer version has been installed. */
export const UpdateBanner = () => {
  const ready = useUpdateReady();
  if (!ready) return null;
  return (
    <View
      accessibilityRole="alert"
      style={{ position: 'absolute', top: 8, left: 12, right: 12, zIndex: 50, flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: colors.navy, borderRadius: 14, paddingVertical: 10, paddingLeft: 14, paddingRight: 8, boxShadow: '0 6px 20px rgba(28, 69, 95,.25)' }}
    >
      <Text variant="bodyStrong" color={colors.textOnDark} style={{ flex: 1 }}>
        Đã có phiên bản mới
      </Text>
      <Pressable
        accessibilityRole="button"
        onPress={() => window.location.reload()}
        style={({ pressed }) => ({ backgroundColor: colors.gold, borderRadius: 999, paddingHorizontal: 14, height: 36, justifyContent: 'center', transform: [{ scale: pressed ? 0.96 : 1 }] })}
      >
        <Text variant="bodyStrong" color={colors.navy}>
          Cập nhật
        </Text>
      </Pressable>
    </View>
  );
};
